const CURRICULUM_ALIASES = {
  CAPS: 'CAPS',
  IEB: 'IEB',
  CAMBRIDGE: 'CAMBRIDGE',
  'PEARSON EDEXEL': 'PEARSON EDEXCEL',
  'PEARSON EDEXCEL': 'PEARSON EDEXCEL',
  EDEXEL: 'PEARSON EDEXCEL',
  EDEXCEL: 'PEARSON EDEXCEL',
  IB: 'IB',
};

const normalizeCurriculumValue = (value) => {
  const normalized = String(value || '').trim().replace(/\s+/g, ' ').toUpperCase();
  return { value: CURRICULUM_ALIASES[normalized] || normalized };
};

const normalizeText = (value) => String(value || '').trim().replace(/\s+/g, ' ');
const normalizeKey = (value) => normalizeText(value).toLowerCase().replace(/&/g, 'and');
const SUBJECT_DISPLAY_ALIASES = {
  maths: 'Mathematics',
  'mathematical literacy': 'Mathematics Literacy',
  'mathematics literacy': 'Mathematics Literacy',
  'math literacy': 'Mathematics Literacy',
  'math lit': 'Mathematics Literacy',
  'english hl': 'English Home Language',
  hl: 'English Home Language',
  'home language': 'English Home Language',
  'english fal': 'English First Additional Language',
  fal: 'English First Additional Language',
  'first additional language': 'English First Additional Language',
  'afrikaans hl': 'Afrikaans Home Language',
  'afrikaans home': 'Afrikaans Home Language',
  'afrikaans home language': 'Afrikaans Home Language',
  'afrikaans fal': 'Afrikaans First Additional Language',
  'afrikaans first additional': 'Afrikaans First Additional Language',
  'afrikaans first additional language': 'Afrikaans First Additional Language',
  'afrikaans additional language': 'Afrikaans First Additional Language',
  ems: 'EMS (Economic & Management Sciences)',
  'economic and management sciences': 'EMS (Economic & Management Sciences)',
  egd: 'Engineering Graphics & Design (EGD)',
  'engineering graphics and design': 'Engineering Graphics & Design (EGD)',
  'information technology (it)': 'Information Technology',
  'information and technology': 'Information Technology',
  it: 'Information Technology',
  'i.t': 'Information Technology',
  'i.t.': 'Information Technology',
  '(it)': 'Information Technology',
  information: 'Information Technology',
  cat: 'Computer Applications Technology (CAT)',
  'cat (computer applications technology)': 'Computer Applications Technology (CAT)',
  'computer applications technology': 'Computer Applications Technology (CAT)',
  'computer application technology': 'Computer Applications Technology (CAT)',
  applications: 'Computer Applications Technology (CAT)',
};

const normalizeSubjectDisplay = (value) => {
  const text = normalizeText(value);
  if (!text) return '';
  return SUBJECT_DISPLAY_ALIASES[normalizeKey(text)] || text;
};

const unique = (values = []) => {
  const seen = new Set();
  const out = [];
  for (const value of values) {
    const normalized = normalizeSubjectDisplay(value);
    if (!normalized) continue;
    const key = normalizeKey(normalized);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(normalized);
  }
  return out;
};

const gradeNumber = (value) => {
  const normalized = normalizeText(value).toUpperCase();
  if (!normalized) return NaN;
  if (normalized === 'R' || normalized === 'GRADE R') return 0;
  const match = normalized.match(/\d+/);
  const parsed = match ? Number(match[0]) : Number(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const subjects = (...items) => items;

const BOOKING_SUBJECT_CATALOG = {
  CAPS: [
    {
      key: 'caps-foundation',
      label: 'Foundation Phase (Grade R-3)',
      minGrade: 0,
      maxGrade: 3,
      subjects: subjects(
        'Mathematics', 'English Home Language', 'Afrikaans Home Language',
        'English First Additional Language', 'Afrikaans First Additional Language',
        'Life Skills', 'isiZulu', 'isiXhosa', 'Sepedi', 'Setswana', 'Sesotho',
        'XiTsonga', 'Tshivenda', 'Siswati', 'isiNdebele'
      ),
    },
    {
      key: 'caps-intermediate',
      label: 'Intermediate Phase (Grade 4-6)',
      minGrade: 4,
      maxGrade: 6,
      subjects: subjects(
        'Mathematics', 'English Home Language', 'English First Additional Language',
        'Afrikaans First Additional Language', 'Afrikaans Home Language',
        'Natural Sciences & Technology', 'Social Sciences', 'Life Skills',
        'Coding & Robotics', 'isiZulu', 'isiXhosa', 'Sepedi', 'Setswana',
        'Sesotho', 'XiTsonga', 'Tshivenda', 'Siswati', 'isiNdebele'
      ),
    },
    {
      key: 'caps-senior',
      label: 'Senior Phase (Grade 7-9)',
      minGrade: 7,
      maxGrade: 9,
      subjects: subjects(
        'Mathematics', 'English FAL', 'English HL', 'Afrikaans FAL',
        'Afrikaans HL', 'Natural Sciences', 'EMS', 'Social Sciences',
        'Technology', 'Coding & Robotics', 'Creative Arts', 'Life Orientation',
        'isiZulu', 'isiXhosa', 'Sepedi', 'Setswana', 'Sesotho', 'XiTsonga',
        'Tshivenda', 'Siswati', 'isiNdebele'
      ),
    },
    {
      key: 'caps-fet',
      label: 'FET Phase (Grade 10-12)',
      minGrade: 10,
      maxGrade: 12,
      subjects: subjects(
        'Mathematics', 'Physical Sciences', 'English HL', 'English FAL',
        'Afrikaans HL', 'Afrikaans Additional Language', 'Life Sciences',
        'Accounting', 'Business Studies', 'Economics', 'Geography',
        'Mathematical Literacy', 'Information Technology',
        'Computer Applications Technology (CAT)', 'History', 'EGD', 'Tourism',
        'Consumer Studies', 'Agricultural Sciences', 'Afrikaans', 'isiZulu',
        'isiXhosa', 'Sepedi', 'Setswana', 'Sesotho', 'XiTsonga', 'Tshivenda',
        'Siswati', 'isiNdebele', 'French', 'German', 'Portuguese', 'Spanish',
        'Mandarin Chinese', 'Arabic', 'Visual Arts', 'Dramatic Arts', 'Music',
        'Religion Studies', 'Life Orientation'
      ),
    },
  ],
  IEB: [
    {
      key: 'ieb-primary',
      label: 'Primary School',
      minGrade: 0,
      maxGrade: 9,
      subjects: subjects(
        'Mathematics', 'English HL', 'English FAL', 'Afrikaans FAL',
        'Afrikaans HL', 'Natural Sciences', 'Social Sciences', 'Technology',
        'Coding & Robotics', 'Creative Arts', 'Life Skills', 'isiZulu',
        'isiXhosa', 'French', 'German', 'Portuguese', 'Spanish',
        'Mandarin Chinese'
      ),
    },
    {
      key: 'ieb-high',
      label: 'High School (Grade 10-12)',
      minGrade: 10,
      maxGrade: 12,
      subjects: subjects(
        'Mathematics', 'Physical Sciences', 'English', 'Life Sciences',
        'Accounting', 'Business Studies', 'Economics', 'Geography',
        'Mathematics Literacy', 'Afrikaans Home Language',
        'Afrikaans First Additional Language', 'Information Technology',
        'Computer Applications Technology', 'History',
        'EGD', 'Tourism', 'Consumer Studies', 'Agricultural Sciences',
        'Afrikaans', 'isiZulu', 'isiXhosa', 'French', 'German', 'Portuguese',
        'Spanish', 'Mandarin Chinese', 'Arabic', 'Visual Arts', 'Dramatic Arts',
        'Music', 'Life Orientation'
      ),
    },
  ],
  CAMBRIDGE: [
    {
      key: 'cambridge-primary',
      label: 'Cambridge Primary',
      levelKeys: ['primary', 'cambridge primary'],
      subjects: subjects('Mathematics', 'English', 'Science', 'Computing', 'Global Perspectives', 'Digital Literacy', 'French', 'Spanish', 'German', 'Mandarin Chinese', 'Arabic', 'Portuguese'),
    },
    {
      key: 'cambridge-lower-secondary',
      label: 'Cambridge Lower Secondary',
      levelKeys: ['lower secondary', 'cambridge lower secondary'],
      subjects: subjects('Mathematics', 'English', 'Science', 'Computing', 'Global Perspectives', 'Geography', 'History', 'French', 'Spanish', 'German', 'Mandarin Chinese', 'Arabic', 'Portuguese', 'Art & Design', 'Physical Education'),
    },
    {
      key: 'cambridge-igcse',
      label: 'Cambridge IGCSE',
      levelKeys: ['igcse', 'o-level', 'o level', 'igcse / o-level'],
      subjects: subjects('Mathematics', 'English Language', 'Physics', 'Chemistry', 'Biology', 'Combined Science', 'Business Studies', 'Economics', 'Accounting', 'Computer Science', 'ICT', 'Geography', 'History', 'English Literature', 'Environmental Management', 'Global Perspectives', 'French', 'Spanish', 'German', 'Mandarin Chinese', 'Arabic', 'Portuguese', 'Italian', 'Russian', 'Japanese', 'Turkish', 'Sociology', 'Psychology', 'Travel & Tourism', 'Art & Design', 'Design & Technology'),
    },
    {
      key: 'cambridge-as-a-level',
      label: 'Cambridge AS & A Level',
      levelKeys: ['as level', 'a-level', 'a level', 'cambridge as and a level'],
      subjects: subjects('Mathematics', 'Further Mathematics', 'Physics', 'Chemistry', 'Biology', 'Computer Science', 'Economics', 'Business', 'Accounting', 'Geography', 'Psychology', 'History', 'Law', 'Sociology', 'Environmental Management', 'English Literature', 'French', 'Spanish', 'German', 'Mandarin Chinese', 'Arabic', 'Portuguese', 'Italian', 'Russian', 'Japanese', 'Turkish', 'Media Studies', 'Travel & Tourism', 'Art & Design'),
    },
  ],
  'PEARSON EDEXCEL': [
    {
      key: 'pearson-primary-lower-secondary',
      label: 'Primary & Lower Secondary',
      levelKeys: ['primary', 'lower secondary', 'primary and lower secondary'],
      subjects: subjects('Mathematics', 'English', 'Science', 'Computing', 'Global Citizenship', 'French', 'Spanish', 'German', 'Mandarin Chinese', 'Arabic'),
    },
    {
      key: 'pearson-international-gcse',
      label: 'International GCSE',
      levelKeys: ['international gcse', 'gcse'],
      subjects: subjects('Mathematics', 'English Language', 'Physics', 'Chemistry', 'Biology', 'Business', 'Economics', 'Accounting', 'Computer Science', 'ICT', 'Geography', 'History', 'English Literature', 'Further Mathematics', 'Human Biology', 'French', 'Spanish', 'German', 'Mandarin Chinese', 'Arabic', 'Portuguese', 'Italian', 'Russian', 'Japanese', 'Hindi', 'Swahili', 'Religious Studies', 'Art & Design'),
    },
    {
      key: 'pearson-international-as-a-level',
      label: 'International AS & A Level',
      levelKeys: ['international as', 'international a-level', 'international a level', 'as level', 'a-level', 'a level'],
      subjects: subjects('Mathematics', 'Further Mathematics', 'Physics', 'Chemistry', 'Biology', 'Computer Science', 'Economics', 'Business', 'Accounting', 'Geography', 'Psychology', 'History', 'Law', 'Politics', 'English Literature', 'French', 'Spanish', 'German', 'Mandarin Chinese', 'Arabic', 'Portuguese', 'Italian'),
    },
  ],
  IB: [
    {
      key: 'ib-pyp',
      label: 'PYP',
      levelKeys: ['pyp', 'primary years'],
      subjects: subjects('Mathematics', 'English', 'Science', 'Social Studies', 'Arts', 'Physical Education', 'French', 'Spanish', 'Mandarin Chinese', 'Arabic'),
    },
    {
      key: 'ib-myp',
      label: 'MYP',
      levelKeys: ['myp', 'middle years'],
      subjects: subjects('Mathematics', 'Extended Mathematics', 'English Language & Literature', 'Biology', 'Chemistry', 'Physics', 'Geography', 'History', 'Economics', 'Business Studies', 'Design & Technology', 'Computer Science', 'Coding & Robotics', 'Visual Arts', 'Music', 'Drama', 'Physical & Health Education', 'French', 'Spanish', 'German', 'Mandarin Chinese', 'Arabic', 'Portuguese', 'Japanese'),
    },
    {
      key: 'ib-dp',
      label: 'DP',
      levelKeys: ['dp', 'diploma', 'cp', 'career-related'],
      subjects: subjects('English', 'Afrikaans', 'French', 'Spanish', 'German', 'Arabic', 'Mandarin Chinese', 'Portuguese', 'Russian', 'Japanese', 'Korean', 'Italian', 'English B', 'French B', 'Spanish B', 'German B', 'Arabic B', 'Mandarin B', 'Portuguese B', 'Russian B', 'Japanese B', 'Italian B', 'Hindi B', 'Swahili B', 'Turkish B', 'Business Management', 'Economics', 'Geography', 'History', 'Psychology', 'Global Politics', 'Philosophy', 'Digital Society', 'Biology', 'Chemistry', 'Physics', 'Computer Science', 'Design Technology', 'Environmental Systems & Societies', 'Sports, Exercise & Health Science', 'Mathematics: Analysis & Approaches', 'Mathematics: Applications & Interpretation', 'Visual Arts', 'Music', 'Theatre', 'Film', 'Dance'),
    },
  ],
};

const levelMatches = (group, curriculumLevel) => {
  const level = normalizeKey(curriculumLevel);
  if (!level) return true;
  return (group.levelKeys || []).some((key) => level.includes(normalizeKey(key)) || normalizeKey(key).includes(level));
};

const gradeMatches = (group, grade) => {
  if (group.minGrade === undefined && group.maxGrade === undefined) return true;
  const numericGrade = gradeNumber(grade);
  if (!Number.isFinite(numericGrade)) return true;
  return numericGrade >= Number(group.minGrade) && numericGrade <= Number(group.maxGrade);
};

const canonicalCurriculum = (value) => normalizeCurriculumValue(value).value || normalizeText(value).toUpperCase();

const getSubjectGroupsForStudent = ({ curriculum, grade, curriculumLevel } = {}) => {
  const canonical = canonicalCurriculum(curriculum);
  const catalog = BOOKING_SUBJECT_CATALOG[canonical] || [];
  const groups = catalog.filter((group) => gradeMatches(group, grade) && levelMatches(group, curriculumLevel));
  const selectedGroups = groups.length ? groups : catalog;
  return selectedGroups.map((group) => ({
    key: group.key,
    label: group.label,
    subjects: unique(group.subjects),
  }));
};

const getSubjectOptionsForStudent = (context = {}) =>
  unique(getSubjectGroupsForStudent(context).flatMap((group) => group.subjects));

const normalizeSubjectName = (value) => normalizeKey(normalizeSubjectDisplay(value));

const isSubjectAllowedForStudent = (subject, context = {}) => {
  const selected = normalizeSubjectName(subject);
  if (!selected) return false;
  return getSubjectOptionsForStudent(context).some((allowed) => normalizeSubjectName(allowed) === selected);
};

const findInvalidSubjectsForStudent = (selectedSubjects = [], context = {}) => {
  const values = unique(Array.isArray(selectedSubjects) ? selectedSubjects : [selectedSubjects]);
  return values.filter((subject) => !isSubjectAllowedForStudent(subject, context));
};


const normalizeArray = (value) => {
  if (Array.isArray(value)) return value.map((entry) => String(entry || '').trim()).filter(Boolean);
  if (value === undefined || value === null || value === '') return [];
  return String(value).split(/[,;]+/).map((entry) => entry.trim()).filter(Boolean);
};

const buildSubjectContexts = ({ role, curricula, grade, gradeLevels, curriculumLevel } = {}) => {
  const normalizedRole = String(role || '').toLowerCase();
  const curriculumValues = normalizeArray(curricula);
  const grades = normalizedRole === 'student'
    ? (grade === undefined || grade === null || grade === '' ? [] : [String(grade)])
    : normalizeArray(gradeLevels).length
      ? normalizeArray(gradeLevels)
      : (grade === undefined || grade === null || grade === '' ? [] : [String(grade)]);
  const gradeValues = grades.length ? grades : [undefined];

  return curriculumValues.flatMap((curriculum) =>
    gradeValues.map((gradeValue) => ({ curriculum, grade: gradeValue, curriculumLevel }))
  );
};

export const subjectNameAllowedForCurriculum = (subjectName, contexts = []) => {
  const subjectKey = normalizeSubjectName(subjectName);
  return contexts.some((context) =>
    getSubjectOptionsForStudent(context).some((allowed) => normalizeSubjectName(allowed) === subjectKey)
  );
};

export const filterSubjectsForCurriculum = (subjectsCatalog = [], context = {}) => {
  const contexts = buildSubjectContexts(context);
  if (!contexts.length) return [];
  return (Array.isArray(subjectsCatalog) ? subjectsCatalog : []).filter((subject) =>
    subjectNameAllowedForCurriculum(subject?.name, contexts)
  );
};

export {
  BOOKING_SUBJECT_CATALOG,
  findInvalidSubjectsForStudent,
  getSubjectGroupsForStudent,
  getSubjectOptionsForStudent,
  isSubjectAllowedForStudent,
  normalizeSubjectName,
};
